import type { ApsPrefillUnmappedTag } from '@common/utils/apsPrefillTagMapping';
import {
    Box,
    Checkbox,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

interface Props {
    presets: ApsPrefillUnmappedTag[];
    selected: string[];
    onToggleAll(): void;
    onToggleRow(tag: string): void;
}

export default function PreconfiguredQuestionsTab({
    presets,
    selected,
    onToggleAll,
    onToggleRow,
}: Props) {
    const allSelected = presets.length > 0 && selected.length === presets.length;
    const someSelected = selected.length > 0 && selected.length < presets.length;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                flex: 1,
                minHeight: 0,
                height: '100%',
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <Typography variant="h3">Preconfigured questions</Typography>
                <Typography variant="body2">
                    Select APS fields that are not on this form yet. Adding them inserts tagged
                    questions with recommended labels and types at the bottom of this group.
                </Typography>
            </Box>
            {presets.length === 0 ? (
                <Typography variant="body2">
                    Every APS prefill question is already on this form.
                </Typography>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={someSelected}
                                        checked={allSelected}
                                        onChange={onToggleAll}
                                        inputProps={{
                                            'aria-label': 'select all preconfigured questions',
                                        }}
                                    />
                                </TableCell>
                                <TableCell>Tag</TableCell>
                                <TableCell>Recommended question</TableCell>
                                <TableCell>Example APS value</TableCell>
                                <TableCell>Type</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {presets.map((preset) => {
                                const isSelected = selected.includes(preset.tag);
                                return (
                                    <TableRow
                                        key={preset.tag}
                                        hover
                                        selected={isSelected}
                                        onClick={() => onToggleRow(preset.tag)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell
                                            padding="checkbox"
                                            onClick={(event) => event.stopPropagation()}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={() => onToggleRow(preset.tag)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={preset.tagLabel}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{preset.recommendedLabel}</TableCell>
                                        <TableCell>{preset.exampleValue}</TableCell>
                                        <TableCell>{preset.questionTypeLabel}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
